import { AuthSdkError } from '../lib/auth-sdk-error';
import { Logger } from '../lib/logger';
import { LoggerNames } from '../lib/constants';

type TransactionKey = 'login' | 'logout' | 'renew' | 'handle-redirect';
type TransactionEntries = {
  [key in TransactionKey]:
    | {
        inProgress: true;
        promise: Promise<unknown>;
        id: string;
      }
    | {
        inProgress: false;
        promise: null;
        id: null;
      };
};

const defaultTransactions = (): TransactionEntries => ({
  login: { inProgress: false, promise: null, id: null },
  logout: { inProgress: false, promise: null, id: null },
  renew: { inProgress: false, promise: null, id: null },
  'handle-redirect': { inProgress: false, promise: null, id: null },
});

/**
 * TransactionManager is used to prevent duplicate operations from running in parallel.
 * Many of the operations in the OIDC flow are not idempotent, so we need to ensure that
 * only a single operation of a given type is in progress at a time.
 *
 * This class should be treated as a singleton. The `getSingleton` method should be used to
 * get the instance of the TransactionManager.
 */
export class TransactionManager {
  private static transactionManager: TransactionManager;
  private transactions: TransactionEntries;
  private readonly logger: Logger;

  constructor() {
    this.transactions = defaultTransactions();
    this.logger = Logger.createLogger(LoggerNames.TRANSACTION_MANAGER);
  }

  /**
   * Used to create a new transaction. If a transaction of the same type is already in progress,
   * this will return a rejected promise.
   *
   * Receives a callback function that returns a promise. The promise is registered as the current transaction.
   * Once it has resolved or rejected, the transaction is cleared.
   *
   * If not locked, `create` will return the promise returned by the callback function. This can be awaited to get the result of the transaction.
   *
   * @param key
   * @param p
   */
  public create<T>(key: TransactionKey, p: () => Promise<T>): Promise<T> {
    /**
     * IMPORTANT
     * This method returns a promise, but it is *not* an async function.
     * The checks for transaction status must be synchronous, or a race condition could occur
     * where the transaction resolves before the check promise resolves.
     * We use the inProgress flag to check the transaction status.
     * This flag is reset after the promise resolves.
     */

    if (this.locked(key)) {
      // Reject instead of throwing so that the error can be handled asynchronously
      this.logger.warn(`A Transaction of type ${key} is already in progress`);
      return Promise.reject(new AuthSdkError(`A Transaction of type ${key} is already in progress`));
    }

    // Create a unique id so we don't overwrite a transaction in progress
    const id = crypto.randomUUID();
    const promise: Promise<T> = p();
    this.transactions[key] = { inProgress: true, promise, id };
    // If the promise resolves or rejects, we need to clean up the transaction
    promise.then(this.finishTransaction(key, id), this.finishTransaction(key, id));
    // Return the original promise so that the return value remains unchanged
    return promise;
  }

  /**
   * Check if a transaction is currently locked (in-progress)
   * @param key
   */
  public locked(key: TransactionKey): boolean {
    return this.transactions[key].inProgress;
  }

  /**
   * Get the promise currently associated with a transaction
   * Useful for de-duping operations while still returning the result of the original operation
   * @param key
   */
  public get<T>(key: TransactionKey): Promise<T> | null {
    return this.transactions[key].promise as Promise<T>;
  }

  /**
   * Clear all transactions.
   * Note that this does not cancel any in-progress promises,
   * it only removes their references from the transaction manager.
   */
  public clear() {
    this.transactions = defaultTransactions();
  }

  /**
   * Reset a transaction key after the original promise resolves/rejects
   * @param key
   * @param id
   * @private
   */
  private finishTransaction(key: TransactionKey, id: string) {
    return () => {
      // We check transaction key and id to ensure that we don't clear a transaction that was overwritten
      if (id === this.transactions[key].id) {
        this.transactions[key] = { inProgress: false, promise: null, id: null };
      }
    };
  }

  private static initTransactionManager() {
    this.transactionManager = new TransactionManager();
    return this.transactionManager;
  }

  public static getSingleton(): TransactionManager {
    if (!this.transactionManager) {
      return this.initTransactionManager();
    }

    return this.transactionManager;
  }
}
